import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  InputAdornment,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import {
  type ChannelConfigurationDto,
  activeThresholdKey,
  SIGNAL_METHOD_UNITS,
} from '@/services/api/classificationService';
import { SignalMethodSelector } from './SignalMethodSelector';

interface Props {
  config: ChannelConfigurationDto;
  onChange: (updates: Partial<ChannelConfigurationDto>) => void;
  onSave: () => void;
  saving: boolean;
}

function toggleAnomalyCheck(current: string[], code: string, enabled: boolean): string[] {
  return enabled ? current.filter((c) => c !== code) : [...current.filter((c) => c !== code), code];
}

interface FieldRow {
  key: keyof ChannelConfigurationDto;
  label: string;
  unit: string;
  step?: number;
}

interface AnomalyCheckGroup {
  code: string;
  label: string;
  rows: FieldRow[];
}

const ANOMALY_CHECK_GROUPS: AnomalyCheckGroup[] = [
  {
    code: 'curve_radius',
    label: 'Curve Radius Check',
    rows: [
      { key: 'curveRadiusMin', label: 'Curve Radius Min', unit: 'Ω' },
      { key: 'curveRadiusMax', label: 'Curve Radius Max', unit: 'Ω' },
    ],
  },
  {
    code: 'impedance_range',
    label: 'Impedance Range Check',
    rows: [
      { key: 'maxImaginaryMin', label: 'Impedance Range Min', unit: 'Ω' },
      { key: 'maxImaginaryMax', label: 'Impedance Range Max', unit: 'Ω' },
    ],
  },
  {
    code: 'series_drift',
    label: 'Series Resistance Drift Check',
    rows: [
      { key: 'rsDriftLimit', label: 'Rs Drift Limit', unit: 'Ω' },
    ],
  },
  {
    code: 'slope_reversal',
    label: 'Slope Reversal Check',
    rows: [
      { key: 'slopeReversalSensitivity', label: 'Slope Reversal Sensitivity', unit: '', step: 0.001 },
    ],
  },
  {
    code: 'inflection_point',
    label: 'Inflection Point Check',
    rows: [
      { key: 'inflectionPointSensitivity', label: 'Inflection Point Sensitivity', unit: '', step: 0.1 },
    ],
  },
  {
    code: 'hook_detection',
    label: 'Hook Detection Check',
    rows: [
      { key: 'hookDetectionTolerance', label: 'Hook Detection Tolerance', unit: 'Ω' },
    ],
  },
];

interface ThresholdRow {
  method: string;
  label: string;
  upperKey: keyof ChannelConfigurationDto;
  lowerKey: keyof ChannelConfigurationDto;
}

const THRESHOLD_ROWS: ThresholdRow[] = [
  { method: 'MaxImaginary',    label: 'Max Imaginary',    upperKey: 'classificationThreshold',  lowerKey: 'lowerThresholdMaxImaginary' },
  { method: 'NyquistArea',     label: 'Nyquist Area',     upperKey: 'thresholdNyquistArea',     lowerKey: 'lowerThresholdNyquistArea' },
  { method: 'FreqArea',        label: 'Frequency Area',   upperKey: 'thresholdFreqArea',        lowerKey: 'lowerThresholdFreqArea' },
  { method: 'AscendingArcArea',label: 'Ascending Arc Area',upperKey: 'thresholdAscendingArcArea',lowerKey: 'lowerThresholdAscendingArcArea' },
  { method: 'MaxImaginaryInterpolated',label: 'Max Imaginary Interpolated',upperKey: 'thresholdMaxImaginaryInterpolated',lowerKey: 'lowerThresholdMaxImaginaryInterpolated' },
  { method: 'MaxImaginaryAbsolute', label: 'Max Imaginary Absolute', upperKey: 'thresholdMaxImaginaryAbsolute', lowerKey: 'lowerThresholdMaxImaginaryAbsolute' },
];

export function ActiveConfigurationPanel({ config, onChange, onSave, saving }: Props) {
  const activeUpperKey = activeThresholdKey(config.signalExtractionMethod);

  const handleNumberChange = (key: keyof ChannelConfigurationDto, raw: string) => {
    const v = parseFloat(raw);
    if (!isNaN(v)) onChange({ [key]: v });
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="subtitle1" fontWeight={600}>
        Active Configuration — CH{config.channelId}
      </Typography>

      {/* Signal method selector */}
      <Box sx={{ maxWidth: 360 }}>
        <SignalMethodSelector
          value={config.signalExtractionMethod}
          onChange={(method) => onChange({ signalExtractionMethod: method })}
          disabled={saving}
        />
      </Box>

      {/* Threshold rows — active one highlighted */}
      <Accordion defaultExpanded disableGutters elevation={0} sx={{ border: 1, borderColor: 'divider', '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">Classification Thresholds</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Method</TableCell>
                <TableCell align="right">Neg. cutoff</TableCell>
                <TableCell align="right">Pos. cutoff</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {THRESHOLD_ROWS.map(({ method, label, upperKey, lowerKey }) => {
                const isActive = upperKey === activeUpperKey;
                const unit = SIGNAL_METHOD_UNITS[method] ?? '%';
                return (
                  <TableRow
                    key={method}
                    sx={isActive ? { bgcolor: 'action.selected' } : { opacity: 0.55 }}
                  >
                    <TableCell>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2">{label}</Typography>
                        {isActive && (
                          <Typography variant="caption" color="primary" fontWeight={600}>
                            ACTIVE
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        size="small"
                        type="number"
                        value={(config[lowerKey] as number).toFixed(2)}
                        onChange={(e) => handleNumberChange(lowerKey, e.target.value)}
                        disabled={saving}
                        InputProps={{ endAdornment: <InputAdornment position="end">{unit}</InputAdornment> }}
                        inputProps={{ step: 0.1 }}
                        sx={{ width: 110 }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <TextField
                        size="small"
                        type="number"
                        value={(config[upperKey] as number).toFixed(2)}
                        onChange={(e) => handleNumberChange(upperKey, e.target.value)}
                        disabled={saving}
                        InputProps={{ endAdornment: <InputAdornment position="end">{unit}</InputAdornment> }}
                        inputProps={{ step: 0.1 }}
                        sx={{ width: 110 }}
                      />
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </AccordionDetails>
      </Accordion>

      {/* Anomaly check thresholds */}
      <Accordion defaultExpanded disableGutters elevation={0} sx={{ border: 1, borderColor: 'divider', '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="subtitle2">Anomaly Check Thresholds</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Parameter</TableCell>
                <TableCell align="right">Value</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ANOMALY_CHECK_GROUPS.map(({ code, label, rows }) => {
                const isDisabled = config.disabledAnomalyChecks.includes(code);
                return [
                  <TableRow key={`${code}-header`} sx={{ bgcolor: 'action.hover' }}>
                    <TableCell colSpan={2} sx={{ py: 0.5 }}>
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={!isDisabled}
                            disabled={saving}
                            onChange={(e) => onChange({
                              disabledAnomalyChecks: toggleAnomalyCheck(config.disabledAnomalyChecks, code, e.target.checked),
                            })}
                          />
                        }
                        label={<Typography variant="caption" fontWeight={600}>{label}</Typography>}
                      />
                    </TableCell>
                  </TableRow>,
                  ...rows.map(({ key, label: rowLabel, unit, step }) => (
                    <TableRow key={key} sx={{ opacity: isDisabled ? 0.4 : 1 }}>
                      <TableCell sx={{ pl: 3 }}>{rowLabel}</TableCell>
                      <TableCell align="right">
                        <TextField
                          size="small"
                          type="number"
                          value={(config[key] as number).toFixed(step === 0.001 ? 4 : 1)}
                          onChange={(e) => handleNumberChange(key, e.target.value)}
                          disabled={saving}
                          InputProps={unit ? { endAdornment: <InputAdornment position="end">{unit}</InputAdornment> } : {}}
                          inputProps={{ step: step ?? 1, min: 0 }}
                          sx={{ width: 140 }}
                        />
                      </TableCell>
                    </TableRow>
                  )),
                ];
              })}
            </TableBody>
          </Table>
        </AccordionDetails>
      </Accordion>

      {/* Metadata */}
      {(config.lastCalibratedAt || config.calibratedBy) && (
        <Stack direction="row" spacing={3}>
          {config.lastCalibratedAt && (
            <Typography variant="caption" color="text.secondary">
              Last calibrated: {new Date(config.lastCalibratedAt).toLocaleString()}
            </Typography>
          )}
          {config.calibratedBy && (
            <Typography variant="caption" color="text.secondary">
              By: {config.calibratedBy}
            </Typography>
          )}
        </Stack>
      )}

      {/* Save button */}
      <Box>
        <Button
          variant="contained"
          color="primary"
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          onClick={onSave}
        >
          {saving ? 'Saving…' : `Save CH${config.channelId} Configuration`}
        </Button>
      </Box>
    </Paper>
  );
}
